import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { store } from '@app/store';
import { setNotification } from '@store/features/notification';
import { IAuthState, setSessionExpired } from '@store/features/auth';
import { GENERAL_ERROR_MESSAGE } from '@utils/constants';
import logger from './logger';
import config from '@/app/configs/config';

const instance = axios.create({
  baseURL: config.api,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Sign requests with jwt if exists
instance.interceptors.request.use(
  async (axiosConfig: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    const authState: IAuthState = store.getState().auth;
    const token = authState.token;

    if (axiosConfig.headers === undefined) {
      axiosConfig.headers = {};
    }
    if (token) {
      axiosConfig.headers.Authorization = `Bearer ${token}`;
    }

    return axiosConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  async (response: AxiosResponse): Promise<AxiosResponse> => {
    return response;
  },
  async (error) => {
    // Check if the request is canceled by our app
    if (error instanceof axios.Cancel) {
      return Promise.reject(error);
    }
    // Cycle dependency
    if (!navigator.onLine) {
      store.dispatch(
        setNotification({
          message: 'Please check your internet connection',
          variant: 'error',
        })
      );
    } else {
      let errorMessage = GENERAL_ERROR_MESSAGE;

      logger.error(error, 'axios error');

      if (error?.response?.status === 401) {
        // Shows the Sign In screen (see AppRouter); never redirects the iframe
        store.dispatch(setSessionExpired(true));
      } else if (
        ['post', 'put'].includes(error?.response?.config?.method) &&
        error?.response?.status === 404
      ) {
        store.dispatch(
          setNotification({ message: errorMessage, variant: 'error' })
        );
      } else if (error?.response?.data?.errors?.messages) {
        errorMessage = error?.response?.data?.errors?.messages.toString();
        store.dispatch(
          setNotification({ message: errorMessage, variant: 'error' })
        );
      }
    }

    return Promise.reject(error);
  }
);

export { instance as axios };
